import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Eye, ArrowDownToLine } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import InspectionDocuments from "@/components/InspectionDocuments";
import { searchFileRequest, openDocumentFile } from "./documentSearchFiles";

const prettyRole = (value, t) => {
  const key = String(value || "").toLowerCase();
  if (!key) return "-";
  if (["father", "mother", "child"].includes(key)) return t(`management.documentsRole${key.charAt(0).toUpperCase()}${key.slice(1)}`);
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const expiryBadgeClass = (accent) => {
  if (accent === "error") return "bg-[var(--error-bg)] text-[var(--error)] border-0 text-xs";
  if (accent === "warning") return "bg-[var(--warning-bg)] text-[var(--warning)] border-0 text-xs";
  return "bg-[var(--info-bg)] text-[var(--info)] border-0 text-xs";
};

const getDocFileIndexes = (doc) => {
  const fileIds = Array.isArray(doc?.file_ids)
    ? doc.file_ids.filter(Boolean)
    : (doc?.file_id ? [doc.file_id] : []);
  return fileIds.map((_value, index) => index);
};

const getDocFileName = (doc, fileIndex) => {
  const fileNames = Array.isArray(doc?.file_names)
    ? doc.file_names
    : (doc?.file_name ? [doc.file_name] : []);
  return fileNames[fileIndex] || fileNames[0] || `${doc?.kind || "document"}.bin`;
};

const getSearchRowFileIndexes = (row) => {
  const count = Number(row?.file_count || 0);
  if (!Number.isFinite(count) || count <= 0) return [];
  return Array.from({ length: count }, (_value, index) => index);
};

const getSearchRowFileName = (row, fileIndex) => {
  const fileNames = Array.isArray(row?.file_names) ? row.file_names : [];
  return fileNames[fileIndex] || fileNames[0] || `${row?.kind || "document"}.bin`;
};

export default function DocumentsSearch() {
  const { t } = useI18n();
  const sourceOptions = [
    { value: "all", label: t("management.documentsSourceAll") },
    { value: "staff", label: t("management.documentsSourceStaff") },
    { value: "admission", label: t("management.documentsSourceAdmission") },
    { value: "institution", label: t("management.documentsSourceInstitution") },
  ];
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("all");

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(queryInput.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [queryInput]);

  const { data: searchData = [], isLoading: searchLoading, isFetching } = useQuery({
    queryKey: ["documents-search", query, source],
    queryFn: async () =>
      unwrap(
        await api.get("/documents/search", {
          params: {
            q: query || undefined,
            source,
            limit: 300,
          },
        }),
      ),
  });

  const { data: expiryData, isLoading: expiryLoading } = useQuery({
    queryKey: ["docs-expiring"],
    queryFn: async () => unwrap(await api.get("/documents/expiring?days=90")),
  });

  const expiryBuckets = expiryData?.buckets || {};
  const expiryGroups = [
    { key: "expired", label: t("nursery.expired"), accent: "error" },
    { key: "30d", label: t("nursery.d30"), accent: "error" },
    { key: "60d", label: t("nursery.d60"), accent: "warning" },
    { key: "90d", label: t("nursery.d90"), accent: "info" },
  ];

  const openFile = async (request, fileName, preview) => {
    if (!request) return;
    try {
      await openDocumentFile(request, fileName, preview);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const documentRequest = (doc, fileIndex) => ({
    url: `/documents/${encodeURIComponent(doc.id)}/download`,
    config: { params: { file_index: fileIndex }, responseType: "blob" },
  });
  const viewDocumentFile = (doc, index) =>
    openFile(documentRequest(doc, index), getDocFileName(doc, index), true);
  const downloadDocumentFile = (doc, index) =>
    openFile(documentRequest(doc, index), getDocFileName(doc, index), false);
  const viewSearchDocumentFile = (row, index) =>
    openFile(searchFileRequest(row, index), getSearchRowFileName(row, index), true);
  const downloadSearchDocumentFile = (row, index) =>
    openFile(searchFileRequest(row, index), getSearchRowFileName(row, index), false);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="documents-search-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("management.dcumentsTitle")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("management.dcumentsSubtitle")}
        </p>
      </div>

      <Tabs defaultValue="search" className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-3">
          <TabsTrigger value="search" className="h-full whitespace-normal px-2 py-2 text-xs sm:text-sm" data-testid="documents-option-search-documents">
            {t("management.searchDocumentsOption")}
          </TabsTrigger>
          <TabsTrigger value="expiry" className="h-full whitespace-normal px-2 py-2 text-xs sm:text-sm" data-testid="documents-option-expiry">
            {t("management.documentsExpiryOption")}
          </TabsTrigger>
          <TabsTrigger value="inspection" className="h-full whitespace-normal px-2 py-2 text-xs sm:text-sm" data-testid="documents-option-inspection">
            {t("management.inspectionTab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="space-y-4">
          <Card className="border-[var(--subtle)]">
            <CardContent>
              <div className="p-4 grid gap-4 md:grid-cols-[1fr_220px_auto] items-end">
                <div>
                  <Label className="text-xs uppercase font-semibold">{t("management.documentsSearchLabel")}</Label>
                  <div className="relative mt-1">
                    <Search className="h-4 w-4 text-[var(--text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      name="documents_search"
                      value={queryInput}
                      onChange={(e) => setQueryInput(e.target.value)}
                      className="pl-9"
                      data-testid="documents-search-input"
                      placeholder={t("management.documentsSearchPlaceholder")}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase font-semibold">{t("management.documentsSearchSource")}</Label>
                  <Select value={source} onValueChange={setSource}>
                    <SelectTrigger className="mt-1" data-testid="documents-search-source">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="button"
                  onClick={() => setQuery(queryInput.trim())}
                  className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                  data-testid="documents-search-submit"
                >
                  {isFetching ? t("common.saving") : t("management.documentsSearchButton")}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[var(--subtle)]">
            <CardContent className="p-0 overflow-x-auto">
              {searchLoading ? (
                <Skeleton className="h-64" />
              ) : (
                <table className="w-full text-sm grid-table">
                  <thead>
                    <tr>
                      <th className="text-start px-4 py-3">{t("management.documentsSource")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsName")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsType")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsNumber")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsExpiry")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsReference")}</th>
                      <th className="text-start px-4 py-3">{t("management.documentsFiles")}</th>
                      <th className="text-left px-4 py-3">{t("common.view")}</th>
                      <th className="text-left px-4 py-3">{t("common.download")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchData.map((row) => {
                      const fileIndexes = getSearchRowFileIndexes(row);
                      return (
                        <tr key={row.id} data-testid={`document-search-row-${row.id}`}>
                          <td className="px-4 py-3">
                            <Badge className={row.source === "staff" ? "bg-blue-100 text-blue-800 border-0" : row.source === "admission" ? "bg-orange-100 text-orange-800 border-0" : "bg-green-100 text-green-800 border-0"}>
                              {sourceOptions.find((option) => option.value === row.source)?.label || row.source_label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-medium">
                            {row.subject_name || "-"}
                            {row.person_role && (
                              <div className="text-xs text-[var(--text-muted)] mt-0.5">
                                {prettyRole(row.person_role, t)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">{row.kind || "-"}</td>
                          <td className="px-4 py-3 font-mono-num text-xs">{row.number || "-"}</td>
                          <td className="px-4 py-3 font-mono-num text-xs">{row.expiry_date || "-"}</td>
                          <td className="px-4 py-3 text-xs">{row.reference || "-"}</td>
                          <td className="px-4 py-3 text-xs">{row.file_count || 0}</td>
                          <td className="px-4 py-3">
                            {fileIndexes.length ? (
                              <div className="flex flex-wrap items-center gap-1">
                                {fileIndexes.map((fileIndex) => (
                                  <Button
                                    key={`search-view-${row.id}-${fileIndex}`}
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    title={`${t("common.view")} ${fileIndex + 1}`}
                                    disabled={!searchFileRequest(row, fileIndex)}
                                    onClick={() => viewSearchDocumentFile(row, fileIndex)}
                                    data-testid={`documents-search-view-${row.id}-${fileIndex}`}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-[var(--text-muted)]">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {fileIndexes.length ? (
                              <div className="flex flex-wrap items-center gap-1">
                                {fileIndexes.map((fileIndex) => (
                                  <Button
                                    key={`search-download-${row.id}-${fileIndex}`}
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    title={`${t("common.download")} ${fileIndex + 1}`}
                                    disabled={!searchFileRequest(row, fileIndex)}
                                    onClick={() => downloadSearchDocumentFile(row, fileIndex)}
                                    data-testid={`documents-search-download-${row.id}-${fileIndex}`}
                                  >
                                    <ArrowDownToLine className="h-4 w-4" />
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-[var(--text-muted)]">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!searchData.length && (
                      <tr>
                        <td colSpan={9} className="text-center py-10 text-[var(--text-muted)]">
                          {t("management.documentsSearchNoResults")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expiry" className="space-y-4" data-testid="documents-expiry-panel">
          {expiryLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <>
              <div className="grid md:grid-cols-4 gap-3">
                {expiryGroups.map((group) => (
                  <Card key={group.key} className="border-[var(--subtle)]" data-testid={`documents-bucket-${group.key}`}>
                    <CardContent className="p-4">
                      <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider">{group.label}</div>
                      <div className="text-3xl font-display font-bold mt-1 font-mono-num">{(expiryBuckets[group.key] || []).length}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-[var(--subtle)]">
                <CardHeader>
                  <CardTitle className="text-sm font-display">{t("nursery.expiring90Title")}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm grid-table">
                      <thead>
                        <tr>
                          <th className="text-left px-3 py-3">{t("nursery.thBucket")}</th>
                          <th className="text-left px-3 py-3">{t("nursery.thPerson")}</th>
                          <th className="text-left px-3 py-3">{t("nursery.thType")}</th>
                          <th className="text-left px-3 py-3">{t("nursery.thKind")}</th>
                          <th className="text-left px-3 py-3">{t("nursery.thNumber")}</th>
                          <th className="text-left px-3 py-3">{t("nursery.thExpires")}</th>
                          <th className="text-left px-3 py-3">{t("common.view")}</th>
                          <th className="text-left px-3 py-3">{t("common.download")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expiryGroups.flatMap((group) => (expiryBuckets[group.key] || []).map((doc) => {
                          const fileIndexes = getDocFileIndexes(doc);
                          return (
                            <tr key={doc.id} data-testid={`documents-expiry-row-${doc.id}`}>
                              <td className="px-3 py-3">
                                <Badge className={expiryBadgeClass(group.accent)}>{group.label}</Badge>
                              </td>
                              <td className="px-3 py-3 font-medium">{doc.subject_name || "-"}</td>
                              <td className="px-3 py-3 text-xs text-[var(--text-muted)]">{doc.subject_type || "-"}</td>
                              <td className="px-3 py-3 text-xs">{doc.kind || "-"}</td>
                              <td className="px-3 py-3 font-mono-num text-xs">{doc.number || "-"}</td>
                              <td className="px-3 py-3 font-mono-num text-xs">{formatDate(doc.expiry_date)}</td>
                              <td className="px-3 py-3">
                                {fileIndexes.length ? (
                                  <div className="flex flex-wrap items-center gap-1">
                                    {fileIndexes.map((fileIndex) => (
                                      <Button
                                        key={`view-${doc.id}-${fileIndex}`}
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        title={`${t("common.view")} ${fileIndex + 1}`}
                                        onClick={() => viewDocumentFile(doc, fileIndex)}
                                        data-testid={`documents-expiry-view-${doc.id}-${fileIndex}`}
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-[var(--text-muted)]">-</span>
                                )}
                              </td>
                              <td className="px-3 py-3">
                                {fileIndexes.length ? (
                                  <div className="flex flex-wrap items-center gap-1">
                                    {fileIndexes.map((fileIndex) => (
                                      <Button
                                        key={`download-${doc.id}-${fileIndex}`}
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        title={`${t("common.download")} ${fileIndex + 1}`}
                                        onClick={() => downloadDocumentFile(doc, fileIndex)}
                                        data-testid={`documents-expiry-download-${doc.id}-${fileIndex}`}
                                      >
                                        <ArrowDownToLine className="h-4 w-4" />
                                      </Button>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-[var(--text-muted)]">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        }))}
                        {(expiryData?.total || 0) === 0 && (
                          <tr>
                            <td colSpan={8} className="py-8 text-center text-[var(--text-muted)]">
                              {t("nursery.noDocsExpiring")}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="inspection" className="space-y-4" data-testid="documents-inspection-panel">
          <InspectionDocuments />
        </TabsContent>
      </Tabs>
    </div>
  );
}
